import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthMe } from 'src/context/AuthMeContext';

interface Props {
  /** Guard by subModuleCode: user must have at least one feature for this submodule. */
  subModuleCode?: string;
  /** Guard by feature code: user must have this specific feature (in any submodule). */
  requiredFeature?: string;
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * Guards a route by checking canAccessSubModule / hasFeature from AuthMeContext.
 * If authMe has not loaded yet (null), renders children optimistically — the backend
 * enforces real security. Once loaded, redirects to /401 if access is denied.
 */
export default function RequireAuthMe({ subModuleCode, requiredFeature, redirectTo = '/401', children }: Props): React.JSX.Element {
  const { authMe, canAccessSubModule, hasFeature } = useAuthMe();

  // Not loaded yet → allow render (backend is the real guard)
  if (!authMe) return <>{children}</>;

  const subModuleAllowed = subModuleCode ? canAccessSubModule(subModuleCode) : true;
  const featureAllowed = requiredFeature ? hasFeature(requiredFeature) : true;
  const allowed = subModuleAllowed && featureAllowed;

  return allowed ? <>{children}</> : <Navigate to={redirectTo} replace />;
}
